/**
 * useErrorReport Hook
 * 
 * Manages error modal state and submits error reports to /api/error-reports.
 * Two modes:
 *   - 'system': Unexpected errors — shows report UI with optional text box + "Send Report"
 *   - 'validation': User input errors — shows message + "OK" dismiss (no report sent)
 * 
 * Auto-captures context: user, organization, role, page route, browser info, timestamp.
 * 
 * Usage:
 *   const { showError, errorState, clearError, submitReport } = useErrorReport();
 *   showError({ title: 'Something Went Wrong', message: err.message, type: 'system' });
 */

"use client";

import { useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useOrganization } from '@/context/OrganizationContext';
import { useUserRole } from '@/context/UserRoleContext';

export type ErrorType = 'system' | 'validation';

export interface ShowErrorOptions {
  title: string;
  message: string;
  type: ErrorType;
  featureArea?: string;
  // Optional server/network details to include in the bug report (HTTP status,
  // raw response body, stack trace, etc.) — hidden from end users, visible on
  // the platform admin Issues Board so we can diagnose without guesswork.
  technicalDetails?: string | Record<string, unknown>;
}

export interface ErrorState {
  isOpen: boolean;
  title: string;
  message: string;
  type: ErrorType;
  featureArea?: string;
  technicalDetails?: string | Record<string, unknown>;
}

interface ErrorContext {
  page: string;
  userAgent: string;
  timestamp: string;
  userId: string | null;
  organizationId: string | null;
  organizationName: string | null;
  userRole: string;
}

const INITIAL_STATE: ErrorState = {
  isOpen: false,
  title: '',
  message: '',
  type: 'validation',
};

export function useErrorReport() {
  const [errorState, setErrorState] = useState<ErrorState>(INITIAL_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { currentUser } = useUserRole();

  const getContext = useCallback((): ErrorContext => {
    return {
      page: typeof window !== 'undefined' ? window.location.pathname : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      timestamp: new Date().toISOString(),
      userId: user?.id ?? null,
      organizationId: currentOrganization?.id ?? null,
      organizationName: currentOrganization?.name ?? null,
      userRole: currentUser?.role ?? currentUser?.activeRole ?? 'unknown',
    };
  }, [user, currentOrganization, currentUser]);

  const showError = useCallback((options: ShowErrorOptions) => {
    setErrorState({
      isOpen: true,
      title: options.title,
      message: options.message,
      type: options.type,
      featureArea: options.featureArea,
      technicalDetails: options.technicalDetails,
    });
  }, []);

  const clearError = useCallback(() => {
    setErrorState(INITIAL_STATE);
  }, []);

  const submitReport = useCallback(async (userComment?: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const context = getContext();

      const featureArea = errorState.featureArea || deriveFeatureArea(context.page);

      await fetch('/api/error-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: errorState.title,
          errorMessage: errorState.message,
          userComment: userComment || '',
          featureArea,
          context,
          technicalDetails: errorState.technicalDetails,
        }),
      });
    } catch {
      // Silently fail — we don't want the error report to cause another error
    } finally {
      setIsSubmitting(false);
    }
  }, [errorState, getContext, isSubmitting]);

  return { showError, errorState, clearError, submitReport, isSubmitting };
}

function deriveFeatureArea(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.includes('users')) return 'staff-management';
  if (segments.includes('parents')) return 'parent-management';
  if (segments.includes('athletes')) return 'athlete-management';
  if (segments.includes('teams')) return 'team-management';
  if (segments.includes('programs')) return 'programs';
  if (segments.includes('billing') || segments.includes('subscription')) return 'billing';
  if (segments.includes('tryouts')) return 'tryouts';
  if (segments.includes('schedule')) return 'schedule';
  if (segments.includes('travel')) return 'travel';
  if (segments.includes('competitions')) return 'competitions';
  if (segments.includes('notifications')) return 'notifications';
  if (segments.includes('policies')) return 'policies';
  if (segments.includes('settings')) return 'settings';
  if (segments.includes('team-builder')) return 'team-builder';

  return segments[segments.length - 1] || 'dashboard';
}
